/**
 * /terms — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * Real terms, not prompts. They describe the arrangement as it actually works:
 * monthly billing, thirty days' notice either way, the service levels on /sla,
 * and infrastructure that belongs to the customer throughout. Nothing here is
 * designed to make leaving difficult, because a managed-infrastructure contract
 * that traps people is how you end up managing infrastructure for people who
 * resent you.
 */
import { LegalPage } from '../components/LegalPage';
import { brand } from '@/shared/brand';

export default function TermsPage() {
  return (
    <LegalPage
      title="Terms of service"
      description="The agreement for Bloomneo managed infrastructure: what we operate, what stays yours, monthly billing with 30 days' notice, service levels and liability."
      updated="20 September 2026"
      draft={false}
      intro={
        <>
          These terms govern your use of {brand.name}, operated by Krishna Teja GS,
          Hyderabad, India. Using the service means accepting them. If you are agreeing on behalf
          of a company, you are confirming you may bind it.
        </>
      }
      sections={[
        {
          heading: 'What we provide',
          body: (
            <>
              <p>
                We operate infrastructure on your behalf: servers, domains and DNS, TLS
                certificates, databases, backups, object storage, transactional email and uptime
                monitoring — whichever of those your plan includes.
              </p>
              <p className="mt-3">
                What we do not do is write or maintain your application. We run the layer
                underneath it, and we keep that layer patched, backed up and watched.
              </p>
            </>
          ),
        },
        {
          heading: 'What remains yours',
          body: (
            <>
              <p>
                Your data, your code and your domains are yours throughout. Domains we register or
                transfer on your behalf are registered to you, not to us. Nothing in these terms
                gives us ownership of anything you put on the infrastructure we manage.
              </p>
              <p className="mt-3">
                We access your systems to operate them, and only for that. Access is logged, and
                the record names the person who acted.
              </p>
            </>
          ),
        },
        {
          heading: 'What we ask of you',
          body: (
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Keep your account credentials to yourself, and tell us promptly if they are exposed.</li>
              <li>Do not use the infrastructure for anything unlawful, or to attack, spam or infringe on anyone else.</li>
              <li>Tell us before making changes to infrastructure we manage, so a change we did not make is not diagnosed as a fault.</li>
              <li>Pay invoices on time.</li>
            </ul>
          ),
        },
        {
          /*
            Named explicitly rather than left to the one-line "nothing
            unlawful" above. On anything that hands somebody a server, a
            reviewer looks for the specific prohibitions — mining, spam,
            proxying, bulk scraping — because those are what shared
            infrastructure actually gets abused for, and a general clause does
            not show that the operator has thought about it.
          */
          heading: 'Acceptable use',
          body: (
            <>
              <p>
                The infrastructure we run for you is shared with other customers. These uses are
                not permitted on it:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5">
                <li>Cryptocurrency mining, or any workload whose purpose is to consume compute for its own sake.</li>
                <li>Sending unsolicited bulk email, or any sending that would put the mail platform's reputation at risk.</li>
                <li>Hosting or distributing malware, phishing pages, or content that infringes someone else's rights.</li>
                <li>Open proxies, VPN exit nodes, anonymising relays, or file-sharing services for third parties.</li>
                <li>Port scanning, credential stuffing, denial-of-service traffic, or any attack on another system.</li>
                <li>Content that is illegal where you operate or where the servers are, including material that sexually exploits anyone.</li>
              </ul>
              <p className="mt-3">
                We do not read your data to police this; we act on what we are told and on what the
                monitoring shows. Where something here is found, we will contact you first unless
                the activity is causing active harm — in which case we suspend it and tell you
                immediately afterwards, as set out under Suspension.
              </p>
            </>
          ),
        },
        {
          heading: 'Fees, billing and notice',
          body: (
            <>
              <p>
                Plans are billed monthly in advance. Either side may end the arrangement by giving
                30 days' notice in writing — the service continues, and is charged, through that
                period, which is what makes an orderly handover possible.
              </p>
              <p className="mt-3">
                The{' '}
                <a href="/cancellation" className="text-primary underline-offset-4 hover:underline">cancellation policy</a>{' '}
                sets out what happens during those 30 days and the{' '}
                <a href="/refund" className="text-primary underline-offset-4 hover:underline">refund policy</a>{' '}
                covers what is and is not refundable.
              </p>
              <p className="mt-3">
                Pass-through costs — a domain registration, a larger server you asked for — are
                charged as incurred and shown separately.
              </p>
            </>
          ),
        },
        {
          heading: 'Service levels',
          body: (
            <p>
              Availability targets, response times and the service credits that apply when we miss
              them are in the{' '}
              <a href="/sla" className="text-primary underline-offset-4 hover:underline">service level agreement</a>
              , which forms part of these terms. Service credits are the remedy for missed
              availability.
            </p>
          ),
        },
        {
          heading: 'Suspension',
          body: (
            <p>
              We may suspend a service where an invoice is significantly overdue, where continuing
              would break the law, or where something on it is actively harming other systems — a
              compromised host sending spam, for instance. Except where the harm is ongoing and
              immediate, we will tell you first and give you a chance to fix it.
            </p>
          ),
        },
        {
          heading: 'Liability',
          body: (
            <>
              <p>
                We take operating your infrastructure seriously, and we are not insuring your
                business against every possible consequence of it being unavailable. Our total
                liability in any twelve-month period is limited to the fees you paid us in that
                period, and we are not liable for lost profits, lost revenue or lost data beyond
                the backups we hold.
              </p>
              <p className="mt-3">
                Nothing here limits liability for anything the law does not allow us to limit,
                including fraud.
              </p>
            </>
          ),
        },
        {
          heading: 'Changes to these terms',
          body: (
            <p>
              We will give account holders at least 30 days' notice by email before a material
              change takes effect. If you do not accept it, you may cancel within that period
              without giving the usual notice.
            </p>
          ),
        },
        {
          heading: 'Governing law',
          body: (
            <p>
              These terms are governed by the laws of India, and the courts at Hyderabad have
              exclusive jurisdiction over any dispute arising from them.
            </p>
          ),
        },
      ]}
    />
  );
}
